import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { Button, Form, Modal } from 'semantic-ui-react';
import InputMask from "react-input-mask";
import { useDidUpdate, useToggle } from '../../lib/hooks';
import { Input } from '../../lib/custom-ui';

import { useForm } from '../../hooks';

import styles from './TaskCloseDateStep.module.scss';

const TaskCloseDateStep = React.memo(({ defaultValue, onApply, onClose }) => {
  const [t] = useTranslation();
  const datePickerDateFormat = t('format:datePickerDate', { defaultValue: 'yyyy.MM.dd' });
  const datePickerDateTimeFormat = t('format:datePickerDateTime', {
    defaultValue: 'yyyy.MM.dd HH:mm',
  });
  
  const [data, handleFieldChange, setData] = useForm(() => {
    const date = defaultValue || new Date();
    return {
      date: t('format:datePickerDate', {
        defaultValue: datePickerDateFormat,
        postProcess: 'formatDate',
        value: date,
      }),
      time: t('format:time', {
        postProcess: 'formatDate',
        value: date,
      }),
    };
  });

  const [selectTimeFieldState, selectTimeField] = useToggle();

  const dateField = useRef(null);
  const timeField = useRef(null);

  const nullableDate = useMemo(() => {
    const date = t('format:datePickerDate', {
      defaultValue: datePickerDateFormat,
      postProcess: 'parseDate',
      value: data.date,
    });

    if (!Number.isNaN(date.getTime())) {
      return date;
    }

    const fallbackDate = t('format:date', {
      postProcess: 'parseDate',
      value: data.date,
    });

    if (Number.isNaN(fallbackDate.getTime())) {
      return null;
    }

    return fallbackDate;
  }, [data.date, t, datePickerDateFormat]);

  const handleDatePickerChange = useCallback(
    (date) => {
      setData((prevData) => ({
        ...prevData,
        date: t('format:datePickerDate', {
          defaultValue: datePickerDateFormat,
          postProcess: 'formatDate',
          value: date,
        }),
      }));
      selectTimeField();
    },
    [setData, selectTimeField, t, datePickerDateFormat],
  );

  const handleApply = useCallback(() => {
    if (!nullableDate) {
      dateField.current.select();
      return;
    }

    const value = t('format:datePickerDateTime', {
      defaultValue: datePickerDateTimeFormat,
      postProcess: 'parseDate',
      value: `${data.date} ${data.time}`,
    });

    if (!Number.isNaN(value.getTime())) {
      onApply(value);
      onClose();
      return;
    }

    const fallbackValue = t('format:dateTime', {
      postProcess: 'parseDate',
      value: `${data.date} ${data.time}`,
    });

    if (Number.isNaN(fallbackValue.getTime())) {
      timeField.current.select();
      return;
    }

    onApply(fallbackValue);
    onClose();
  }, [onApply, onClose, data, nullableDate, t, datePickerDateTimeFormat]);

  useEffect(() => {
    dateField.current.select();
  }, []);

  useDidUpdate(() => {
    if(timeField.current)
      timeField.current.select();
  }, [selectTimeFieldState]);

  return (
    <>
      <Modal.Header>Выбор даты и времени закрытия задачи</Modal.Header>
      <Modal.Content>
        <Form onSubmit={handleApply}>
          <div className={styles.fieldWrapper}>
            <div className={styles.fieldBox}>
              <div className={styles.text}>{t('common.date')}</div>
              <Input ref={dateField} name="date" value={data.date} onChange={handleFieldChange} />
            </div>
            <div className={styles.fieldBox}>
              <div className={styles.text}>{t('common.time')}</div>
              <InputMask
                mask="99:99"
                placeholder="__:__"
                value={data.time}
                onChange={(e) => handleFieldChange(null, { name: "time", value: e.target.value })}
              >
                {(inputProps) => <Input {...inputProps} />}
              </InputMask>
            </div>
          </div>
          <DatePicker
            inline
            disabledKeyboardNavigation
            selected={nullableDate}
            onChange={handleDatePickerChange}
          />
          <Button positive content="Применить" onClick={handleApply} />
        </Form>
      </Modal.Content>
    </>
  );
});

TaskCloseDateStep.propTypes = {
  defaultValue: PropTypes.instanceOf(Date),
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

TaskCloseDateStep.defaultProps = {
  defaultValue: undefined,
};

export default TaskCloseDateStep;
